import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ParticipantsList = ({ 
  participants, 
  billItems, 
  currentUserId = '1',
  onViewUserItems,
  onRemoveParticipant,
  isCollapsed = false 
}) => {
  const [selectedUserId, setSelectedUserId] = useState(currentUserId);

  const calculateUserStats = (userId) => {
    let itemCount = 0;
    let totalAmount = 0;
    
    billItems?.forEach(item => {
      const userClaim = item?.claims?.find(claim => claim?.userId === userId);
      if (userClaim) {
        itemCount += userClaim?.quantity;
        totalAmount += item?.price * userClaim?.quantity;
      }
    });

    const tax = totalAmount * 0.21;
    const finalTotal = totalAmount + tax;

    return { itemCount, subtotal: totalAmount, tax, total: finalTotal };
  };

  const getParticipantStatus = (userId) => {
    const stats = calculateUserStats(userId);
    if (stats?.itemCount === 0) return 'inactive';
    if (stats?.itemCount > 0) return 'active';
    return 'idle';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success';
      case 'idle':
        return 'bg-warning';
      case 'inactive':
        return 'bg-muted';
      default:
        return 'bg-muted';
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(price);
  };

  const getInitials = (name) => {
    return name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  if (isCollapsed) {
    return (
      <div className="w-16 bg-surface border-r border-border h-full">
        <div className="p-2 space-y-2">
          <div className="text-xs text-text-secondary text-center mb-2">
            Usuarios
          </div>
          {participants?.map((participant) => {
            const stats = calculateUserStats(participant?.id);
            const status = getParticipantStatus(participant?.id);
            
            return (
              <Button
                key={participant?.id}
                variant={selectedUserId === participant?.id ? "default" : "ghost"}
                size="icon"
                onClick={() => {
                  setSelectedUserId(participant?.id);
                  onViewUserItems(participant?.id);
                }}
                className="w-12 h-12 relative"
                title={`${participant?.name} - ${formatPrice(stats?.total)}`}
              >
                <div className="text-xs font-medium">
                  {getInitials(participant?.name)}
                </div>
                <div
                  className={`absolute top-1 right-1 w-2 h-2 rounded-full ${getStatusColor(status)}`}
                />
                {participant?.id === currentUserId && (
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="User" size={8} color="white" />
                  </div>
                )}
              </Button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 bg-surface border-r border-border h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-2">
            Participantes
          </h2>
        </div>
        <div>
          {participants?.find(p => String(p?.id) === String(currentUserId)) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e?.stopPropagation(); onRemoveParticipant && onRemoveParticipant(currentUserId); }}
              iconName="LogOut"
              iconSize={14}
              className="text-text-secondary hover:text-foreground"
            >
              Salir
            </Button>
          )}
        </div>
        <p className="text-sm text-text-secondary">
          {participants?.length} persona{participants?.length !== 1 ? 's' : ''} en la mesa
        </p>
      </div>
      {/* Participants List */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-2 space-y-2">
          {participants?.map((participant) => {
            const stats = calculateUserStats(participant?.id);
            const status = getParticipantStatus(participant?.id);
            const isCurrentUser = participant?.id === currentUserId;
            const isSelected = selectedUserId === participant?.id;
            const currentUser = participants?.find(p => p?.id === currentUserId);
            const isCurrentUserHost = currentUser?.isHost === true;

            return (
              <div
                key={participant?.id}
                onClick={() => {
                  setSelectedUserId(participant?.id);
                  onViewUserItems(participant?.id);
                }}
                className={`
                  p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-elevation-1
                  ${isSelected 
                    ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/30'
                  }
                  ${isCurrentUser ? 'ring-2 ring-primary/20' : ''}
                `}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">
                        {getInitials(participant?.name)}
                      </div>
                      <div
                        className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border border-surface ${getStatusColor(status)}`}
                      />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-foreground">
                          {participant?.name}
                        </span>
                        {isCurrentUser && (
                          <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                            Tú
                          </span>
                        )}
                        {participant?.isHost && !isCurrentUser && (
                          <span className="text-xs bg-accent text-accent-foreground px-2 py-0.5 rounded-full">
                            Anfitrión
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-text-secondary">
                        {stats?.itemCount} artículo{stats?.itemCount !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="font-semibold text-foreground">
                      {formatPrice(stats?.total)}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {status === 'active' ? 'Activo' : 
                       status === 'idle' ? 'Inactivo' : 'Sin selección'}
                    </div>
                  </div>
                </div>
                {/* Progress Bar */}
                {stats?.itemCount > 0 && (
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-text-secondary mb-1">
                      <span>Subtotal: {formatPrice(stats?.subtotal)}</span>
                      <span>IVA: {formatPrice(stats?.tax)}</span>
                    </div>
                  </div>
                )}
                {/* Quick Actions */}
                {isSelected && (
                  <div className="mt-3 pt-3 border-t border-border flex space-x-2">
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={(e) => {
                        e?.stopPropagation();
                        onViewUserItems(participant?.id);
                      }}
                      iconName="Eye"
                      iconSize={12}
                    >
                      Ver Items
                    </Button>
                    {isCurrentUser && (
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={(e) => {
                          e?.stopPropagation();
                          // Handle edit profile or settings
                        }}
                        iconName="Settings"
                        iconSize={12}
                      >
                        Ajustes
                      </Button>
                    )}
                    {/* If current user is host, show remove option for others */}
                    {isCurrentUserHost && !isCurrentUser && (
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={(e) => { e?.stopPropagation(); onRemoveParticipant && onRemoveParticipant(participant?.id); }}
                        iconName="X"
                        iconSize={12}
                        className="text-error"
                      >
                        Eliminar
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Footer Stats */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-foreground font-mono">
              {participants?.filter(p => getParticipantStatus(p?.id) === 'active')?.length}
            </div>
            <div className="text-xs text-text-secondary">Activos</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-foreground font-mono">
              {formatPrice(
                participants?.reduce((sum, p) => sum + calculateUserStats(p?.id)?.total, 0)
              )}
            </div>
            <div className="text-xs text-text-secondary">Total Mesa</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipantsList;